import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'

import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth } from '@/lib/api'
import { chameleonKeys } from '@/lib/queryKeys'

interface ChameleonIdentity {
  /** null when CHAMELEON_VERIFICATION_SECRET is unset server-side (local dev). */
  uid_hash: string | null
}

/**
 * Server-generated verification hash for the current user. Chameleon requires it
 * to accept an identify call, and it can only be produced server-side — the
 * signing secret never reaches the browser.
 *
 * Deliberately not `staleTime: Infinity`: the hash is timestamp-anchored, so a
 * cached one goes stale. The app default (5 min) lets a later re-identify pick up
 * a fresh signature instead of replaying an expired one.
 */
export function useChameleonUidHash(
  userId: string | null | undefined,
  orgId: string | null | undefined
) {
  const { getToken } = useAuth()

  const { data } = useQuery({
    queryKey: chameleonKeys.identity(userId, orgId),
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchJsonWithAuth<ChameleonIdentity>({
        token,
        url: `${AGENT_URL}/chameleon/identity`,
        errorMessage: 'Failed to fetch Chameleon identity',
      })
    },
    // The endpoint's auth path reads `org_id`/`org_slug` straight off the Clerk
    // token (hoku/auth.py subscripts them), so it 401s while the app still sits
    // on "Select an Organization". orgId is in the key as well as the gate, so
    // picking an org later actually re-runs the query instead of serving the
    // disabled entry forever.
    enabled: !!userId && !!orgId,
  })

  return data?.uid_hash ?? null
}
